import type { JSX, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';

import { IS_ONBOARDING_REQUIRED } from '../../graphql/onboarding.operations';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import type { UntypedData } from '../../apollo/untypedData';

interface OnboardingGuardProps {
  children: ReactNode;
}

/**
 * Guard component that checks if onboarding is required
 * and redirects to the onboarding flow if needed.
 * 
 * This should wrap the main application layout AFTER
 * authentication and tenant selection is complete.
 */
export function OnboardingGuard({ children }: OnboardingGuardProps): JSX.Element {
  const location = useLocation();

  const { data, loading, error } = useQuery<UntypedData>(IS_ONBOARDING_REQUIRED, {
    fetchPolicy: 'cache-and-network',
    // Don't refetch too aggressively
    pollInterval: 0,
  });

  // Show loading while checking onboarding status
  if (loading && !data) {
    return <LoadingOverlay />;
  }

  // If there's an error, allow access to the app
  // (don't block the entire app due to onboarding check failure)
  if (error) {
    console.warn('Failed to check onboarding status:', error.message);
    return <>{children}</>;
  }

  // Check if onboarding is required
  const isOnboardingRequired = data?.isOnboardingRequired;
  
  // Check if user explicitly chose to exit onboarding during this session
  const hasExitedOnboarding = sessionStorage.getItem('onboardingExited') === 'true';

  if (isOnboardingRequired && !hasExitedOnboarding) {
    // Redirect to onboarding, preserving the intended destination
    return (
      <Navigate
        to="/onboarding"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Onboarding is complete or not required, render children
  return <>{children}</>;
}

export default OnboardingGuard;


